import { slotsFor, periodDates } from './topPlacementOverview';

// Reference prices for a coverage-based estimate, never billing or mutation policy.
export const PRICE_GUIDE = Object.freeze({ bundle: 2700, A: 1500, B: 1000, C: 600 });
const TIERS = Object.freeze({
  38: 'A', 31: 'A', 39: 'A',
  10: 'B', 3: 'B', 7: 'B', 15: 'B', 16: 'B', 2: 'B', 68: 'B', 33: 'B',
  80: 'C', 35: 'C', 57: 'C', 13: 'C', 4: 'C', 27: 'C', 6: 'C', 40: 'C',
  37: 'C', 8: 'C', 41: 'C', 42: 'C', 61: 'C', 36: 'C', 20: 'C', 66: 'C',
});

export function financialProjection(view, catalog, on) {
  if (!view || view.hasMore !== false || !Number.isInteger(view.slotLimit) || view.slotLimit < 1 ||
      !Array.isArray(catalog) || catalog.some(item => !Number.isInteger(item.id) || item.id < 1) ||
      !/^\d{4}-\d{2}-\d{2}$/.test(on || '') || !Number.isFinite(Date.parse(on))) return null;
  const directions = new Map(catalog.map(item => [item.id, item]));
  // The reservations owner supplies confirmed periods; keep the day/status boundary explicit here too.
  const current = { ...view, rows: view.rows.filter(row => {
    const dates = periodDates(row);
    return row.status === 'confirmed' && dates.from <= on && dates.to >= on;
  }) };
  const prices = new Map([...directions.values()].map(direction => {
    const tier = TIERS[direction.id] || 'C';
    return [direction.id, { ...direction, tier, rate: PRICE_GUIDE[tier], baseline: !TIERS[direction.id] }];
  }));
  const groups = new Map();
  current.rows.forEach(row => {
    if (!groups.has(row.clinic_id)) groups.set(row.clinic_id, { clinicId: row.clinic_id, bundle: false, directions: new Set() });
    const group = groups.get(row.clinic_id);
    group.bundle = group.bundle || row.show_on_homepage || row.illness_kind_ids.length === 0;
    row.illness_kind_ids.forEach(id => group.directions.add(id));
  });
  const clinics = [...groups.values()].map(group => {
    const ids = [...group.directions].sort((a, b) => a - b);
    const unknown = group.bundle ? [] : ids.filter(id => !prices.has(id));
    return {
      clinicId: group.clinicId, kind: group.bundle ? 'bundle-estimate' : 'direction-estimate',
      directions: group.bundle ? [] : ids,
      unknown,
      monthly: group.bundle ? PRICE_GUIDE.bundle : ids.reduce((sum, id) => sum + (prices.get(id)?.rate || 0), 0),
    };
  }).sort((a, b) => b.monthly - a.monthly || a.clinicId - b.clinicId);
  const inventory = [...prices.values()].map(direction => {
    const reserved = slotsFor(current, direction.id).length;
    const free = Math.max(0, view.slotLimit - reserved);
    return { ...direction, reserved, free, potential: free * direction.rate };
  }).sort((a, b) => b.potential - a.potential || a.id - b.id);
  const homepageFree = Math.max(0, view.slotLimit - slotsFor(current, 'homepage').length);
  // A bundle consumes overlapping inventory. It is an alternative, never additive upside.
  const bundleFree = inventory.length ? Math.min(homepageFree, ...inventory.map(item => item.free)) : 0;
  const bottlenecks = inventory.filter(item => item.free === bundleFree).map(item => item.name);
  if (homepageFree === bundleFree) bottlenecks.unshift('Homepage');
  return {
    kind: 'reference-price-projection', on,
    clinics, inventory,
    activeMonthly: clinics.reduce((sum, item) => sum + item.monthly, 0),
    unpriced: clinics.reduce((sum, item) => sum + item.unknown.length, 0),
    bundleClinics: clinics.filter(item => item.kind === 'bundle-estimate').length,
    directionPlacements: clinics.reduce((sum, item) => sum + item.directions.length - item.unknown.length, 0),
    freeSlots: inventory.reduce((sum, item) => sum + item.free, 0),
    directionPotential: inventory.reduce((sum, item) => sum + item.potential, 0),
    homepageFree, bundleFree, bundlePotential: bundleFree * PRICE_GUIDE.bundle, bottlenecks,
    baselineDirections: inventory.filter(item => item.baseline).length,
  };
}
